import { callApi } from "./apiService";

let ws = null;
let ready = false;
let onMessageHandler = null;

const PROTOCOL = "json.webpubsub.azure.v1";

function isConnected() {
  return !!ws && ws.readyState === WebSocket.OPEN;
}

/**
 * Connect (or return the existing connection).
 * @param {Object} opts
 * @param {string} opts.apiURL
 * @param {string} [opts.userId="anonymous"]
 * @returns {Promise<WebSocket>}
 */

async function client({ apiURL, userId = "anonymous" } = {}) {
  if (isConnected()) return ws;

  const { data } = await callApi(
    "get",
    `${apiURL}/getWebPubSubToken?userId=${encodeURIComponent(userId)}`
  );

  if (!data || !data.url) {
    throw new Error("No Web PubSub URL returned from API");
  }

  ws = new WebSocket(data.url, PROTOCOL);

  ws.onopen = () => {
    ready = true;
    try {
      ws.send(JSON.stringify({ type: "joinGroup", group: "all" }));
    } catch (err) {
      console.warn("⚠️ Failed to join group 'all':", err);
    }
  };

  ws.onmessage = (evt) => {
    let payload = evt.data;
    try {
      const parsed = JSON.parse(evt.data);
      payload = parsed?.data !== undefined ? parsed.data : parsed;
    } catch (parseErr) {
      console.warn("⚠️ WS message parse error:", parseErr);
    }

    if (onMessageHandler) {
      try {
        onMessageHandler(payload);
      } catch (handlerErr) {
        console.error("⚠️ onMessage handler error:", handlerErr);
      }
    }
  };

  ws.onclose = (evt) => {
    ready = false;
    ws = null;
    // Helpful in dev; harmless in prod
    if (typeof console !== "undefined") {
      console.warn("⚪️ WS closed", {
        code: evt.code,
        reason: evt.reason,
        wasClean: evt.wasClean,
      });
    }
  };

  ws.onerror = (err) => {
    console.error("❌ WebSocket error:", err);
  };

  return ws;
}

function sendToAll(payload = {}) {
  if (!isConnected() || !ready) return;
  const envelope = {
    type: "sendToGroup",
    group: "all",
    dataType: "json",
    data: payload,
  };

  try {
    ws.send(JSON.stringify(envelope));
  } catch (err) {
    console.error("⚠️ Failed to send to group 'all':", err);
  }
}

function onMessage(handler) {
  onMessageHandler = typeof handler === "function" ? handler : null;
}

function close(code = 1000, reason = "client close") {
  if (!ws) {
    ready = false;
    onMessageHandler = null;
    return;
  }
  try {
    ws.close(code, reason);
  } catch (err) {
    console.error("⚠️ WS close error:", err);
  } finally {
    ws = null;
    ready = false;
    onMessageHandler = null;
  }
}

export default { client, isConnected, sendToAll, onMessage, close };
